import { useState } from 'react'

import {
  Box,
  Text,
  WizardStep,
  WizardStepRenderParams,
  useOnNext,
} from '@factorialco/deprecated-design-system'

import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { unifiedAddEmployeeEvents } from 'modules/employees/lib/events'
import { Country } from 'modules/legal_entities/lib/constants'
import idValidationStrategies from 'modules/legal_entities/lib/identifierValidators'
import { SpainIdType } from 'modules/legal_entities/lib/identifierValidators/spain/idType'
import type { IdentificationType } from 'modules/legal_entities/lib/types'

import { Fields } from '../Fields/Fields'
import { WizardData, genericAmplitudeProperties } from '../types'
import { groupFieldsByGroup } from '../utils'

const isCompletedStep = (data: WizardData) => {
  const socialSecurityNumberValid =
    (!!data.socialSecurityNumber && !!data.socialSecurityNumberValid) || !data.socialSecurityNumber
  const identifierValid = (!!data.identifier && !!data.identifierValid) || !data.identifier
  const identifierTypeValid = !(data.identifierType && !data.identifier)

  return socialSecurityNumberValid && identifierValid && identifierTypeValid
}

const PersonalDetailsStep = ({ data, onDataChanged }: WizardStepRenderParams<WizardData>) => {
  const [errors, setErrors] = useState({
    bankNumber: false,
    swiftBic: false,
  })

  const isSpainCompany = data.legalEntityCountry?.toLowerCase() === 'es'
  const fields = data.fieldsSchema?.filter((field) => field.category === 'personal_details')
  const countryFields = fields?.filter((field) => {
    if (field.fieldName === 'social_security_number' && !isSpainCompany) {
      return false
    }
    return true
  })
  const groupedFields = groupFieldsByGroup(countryFields)

  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const countryEnumValue: Country = data.legalEntityCountry as Country

  useOnNext(() => {
    track(unifiedAddEmployeeEvents.addPersonalInformation, genericAmplitudeProperties(data))
  })

  const validation = (fieldName: string, value: string) => {
    if (fieldName === 'identifier') {
      return calculateIdentifierError(value, data.identifierType || '')
    }
    if (fieldName === 'identifierType') {
      return calculateIdentifierError(data.identifier || '', value)
    }
    if (fieldName === 'socialSecurityNumber') {
      return calculateSocialSecurityNumberError(value)
    }
    if (
      fieldName === 'bankNumberFormat' ||
      fieldName === 'swiftBic' ||
      fieldName === 'bankNumber'
    ) {
      calculateBankErrors(value)
    }
  }

  const getFieldError = (fieldName: string): string | undefined => {
    switch (fieldName) {
      case 'identifier':
        return (
          (identifierError &&
            !!data.identifierType &&
            i18n.t('add_employee_modal.personal_details_step.fields.id_number.invalid_error')) ||
          (identifierError &&
            !data.identifierType &&
            i18n.t(
              'add_employee_modal.personal_details_step.fields.id_number.identifier_type_empty_error'
            )) ||
          ''
        )
      case 'socialSecurityNumber':
        return (
          socialSecurityNumberError &&
          i18n.t(
            'add_employee_modal.personal_details_step.fields.social_security_number.invalid_error'
          )
        )
      case 'bankNumber':
        if (!!errors.bankNumber) return i18n.t('employee_form.bank_info.mandatory')
      case 'swiftBic':
        if (!!errors.swiftBic) return i18n.t('employee_form.bank_info.mandatory')
    }
  }

  const validFieldsMap: Record<string, string> = {
    identifier: 'identifierValid',
    identifierType: 'identifierValid',
    socialSecurityNumber: 'socialSecurityNumberValid',
  }

  const onChangeField = (fieldName: string, value: string) => {
    const hasError = validation(fieldName, value)
    const validField = validFieldsMap[fieldName]

    onDataChanged({
      ...data,
      [fieldName]: value,
      ...(validField && { [validField]: !hasError }),
    })
  }

  const calculateSocialSecurityNumberError = (value: string) => {
    if (!value) {
      return ''
    }
    return idValidationStrategies(
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      countryEnumValue as Country,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      'social_security_number' as SpainIdType,
      value,
      'social_security_number'
    ).errors()
  }
  const socialSecurityNumberError = calculateSocialSecurityNumberError(
    data.socialSecurityNumber || ''
  )

  const calculateIdentifierError = (identifier: string, identifierType: string) => {
    if (!identifier) {
      return ''
    }

    const errors = idValidationStrategies(
      countryEnumValue,
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      identifierType as IdentificationType,
      identifier,
      identifierType
    ).errors()

    return errors
  }
  const calculateBankErrors = (value: string) => {
    if (!!data.bankNumberFormat && !data.bankNumber) {
      setErrors({ ...errors, bankNumber: true })
    } else {
      setErrors({ ...errors, bankNumber: false })
    }

    if (
      (value === 'sort_code_and_account_number' ||
        value === 'routing_number_and_account_number' ||
        value === 'bank_name_and_account_number') &&
      !data.swiftBic
    ) {
      setErrors({ ...errors, swiftBic: true })
    } else {
      setErrors({ ...errors, swiftBic: false })
    }
  }
  const identifierError = calculateIdentifierError(data.identifier || '', data.identifierType || '')

  return (
    <Box flexDirection="row" gap="s48">
      <Box flexDirection="column" gap="s24" width="s400">
        <Text.Header2>{i18n.t('add_employee_modal.personal_details_step.header')}</Text.Header2>
        {Object.entries(groupedFields ?? {}).map(([group, groupFields]) => (
          <Box key={group} flexDirection="row" gap="s16">
            {groupFields.map((field) => {
              return (
                <Fields
                  key={field.fieldName}
                  field={field}
                  data={data}
                  onChangeField={onChangeField}
                  getFieldError={getFieldError}
                />
              )
            })}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

export const personalDetailsStep = (): WizardStep<WizardData> => ({
  title: i18n.t('add_employee_modal.personal_details_step.title'),
  nextText: i18n.t('common.next'),
  render: (props) => <PersonalDetailsStep {...props} />,
  isCompleted: isCompletedStep,
})
